import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ScannerPage } from './ScannerPage';
import { TestPage } from './TestPage';
import { APIDocsPage } from './APIDocsPage';

type TabView = 'overview' | 'scanner' | 'test' | 'stats' | 'api' | 'docs';

export const QRSPApp: React.FC = () => {
  const [currentTab, setCurrentTab] = useState<TabView>('overview');
  const [apiHealth, setApiHealth] = useState<'loading' | 'healthy' | 'error'>('loading');

  useEffect(() => {
    checkApiHealth();
  }, []);

  const checkApiHealth = async () => {
    try {
      const response = await fetch('https://cf.qrsp.app/api/health');
      if (response.ok) {
        setApiHealth('healthy');
      } else {
        setApiHealth('error');
      }
    } catch (error) {
      setApiHealth('error');
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: '🏠' },
    { id: 'scanner', name: 'Scanner', icon: '📱' },
    { id: 'test', name: 'Test QR', icon: '🧪' },
    { id: 'stats', name: 'Stats', icon: '📊' },
    { id: 'api', name: 'API', icon: '🔌' },
    { id: 'docs', name: 'API Docs', icon: '📡' }
  ] as const;

  const features = [
    {
      title: 'QR Generation',
      description: 'Generate secure QR codes with JWT tokens (5-minute expiry)',
      icon: '📱',
      status: apiHealth === 'healthy' ? 'Online' : 'Checking...'
    },
    {
      title: 'QR Scanner',
      description: 'Camera-based QR code scanner with real-time validation',
      icon: '📷',
      status: 'Ready'
    },
    {
      title: 'D1 Database',
      description: 'Coupon tracking with drivers, vendors, and redemptions',
      icon: '🗃️',
      status: apiHealth === 'healthy' ? 'Connected' : 'Checking...'
    },
    {
      title: 'PWA Ready',
      description: 'Works offline and can be installed on mobile devices',
      icon: '📲',
      status: 'Active'
    }
  ];

  const renderTabContent = () => {
    switch (currentTab) {
      case 'scanner':
        return <ScannerPage />;
      case 'test':
        return <TestPage />;
      case 'stats':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">📊 Redemption Statistics</h3>
              <p className="text-gray-600">Coming soon: View driver commissions, vendor usage, and redemption trends.</p>
            </div>
          </div>
        );
      case 'docs':
        return <APIDocsPage />;
      case 'api':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">QR Coupon API Endpoints</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">GET /api/health</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    apiHealth === 'healthy' ? 'bg-green-100 text-green-800' :
                    apiHealth === 'error' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {apiHealth === 'healthy' ? 'Online' : apiHealth === 'error' ? 'Offline' : 'Checking'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">POST /api/qr/generate</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Ready</span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">POST /api/qr/validate</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Ready</span>
                </div>
                <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                  <span className="font-mono text-sm">GET /api/coupons</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Ready</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Test API Connection</h3>
              <button
                onClick={checkApiHealth}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Test Connection
              </button>
            </div>
          </div>
        );
      default:
        return (
          <div className="space-y-8">
            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl p-8">
              <h2 className="text-3xl font-bold mb-4">🚀 QR Coupon Platform</h2>
              <p className="text-xl opacity-90 mb-6">
                São Paulo - Uber drivers recruiting vendors for QR coupon system
              </p>
              <div className="text-lg opacity-80">
                Generate, scan, and validate QR codes for discount coupons with real-time tracking.
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-sm border p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="text-3xl">{feature.icon}</div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      feature.status === 'Connected' || feature.status === 'Active' || feature.status === 'Ready'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {feature.status}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>

            {/* Quick Start */}
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-xl font-semibold mb-4">🚀 Quick Start</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <span>Use <strong>Test QR</strong> to generate QR codes for testing</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                  <span>Open <strong>Scanner</strong> to validate QR codes with your camera</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                  <span>Check <strong>Stats</strong> to view redemption analytics</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                  <span>View <strong>API</strong> endpoints for integration</span>
                </div>
              </div>
              
              <div className="mt-6 pt-4 border-t space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">🎯 How it works:</h4>
                  <p className="text-sm text-gray-600">
                    Uber drivers recruit vendors → Vendors create coupons → Customers get QR codes → 
                    Vendors scan to validate → Drivers earn commissions
                  </p>
                </div>
                
                <div>
                  <a
                    href="/demo"
                    className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-colors"
                  >
                    <span>🎬</span>
                    <span>Watch Interactive Demo</span>
                  </a>
                  <p className="text-xs text-gray-500 mt-1">
                    See the complete driver workflow with animations and mock data
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="text-2xl">📱</div>
              <h1 className="text-2xl font-bold text-gray-900">QRSP</h1>
              <span className="text-sm text-gray-500">São Paulo</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                apiHealth === 'healthy' ? 'bg-green-100 text-green-800' :
                apiHealth === 'error' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                <div className={`w-2 h-2 rounded-full ${
                  apiHealth === 'healthy' ? 'bg-green-500' :
                  apiHealth === 'error' ? 'bg-red-500' :
                  'bg-yellow-500'
                }`} />
                <span>API {apiHealth === 'healthy' ? 'Online' : apiHealth === 'error' ? 'Offline' : 'Checking'}</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setCurrentTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  currentTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTabContent()}
      </main>

    </div>
  );
};